import useSWRImmutable from 'swr/immutable';
import type { FormattedGeometry } from '/@/core/fields/geometry';
import settings from '/@/core/settings';
import { revalidateMyFields } from '/@/hooks/fields';
import { axFetch } from '/@/store/session/actions';
import type { PaginatedResponse } from '/@/types/core';
import type { CropType } from '/@/types/crop-monitoring';
import type { Field, FieldViewFieldsResponse, FieldViewImportFieldsResponse } from '/@/types/field';
import { isOldPolygon } from '/@/utils';

const BRUFs = {
    Acre: 'AC',
    Alagoas: 'AL',
    Amapá: 'AP',
    Amazonas: 'AM',
    Bahia: 'BA',
    Ceará: 'CE',
    Distrito_Federal: 'DF',
    Espírito_Santo: 'ES',
    Goiás: 'GO',
    Maranhão: 'MA',
    Mato_Grosso: 'MT',
    Mato_Grosso_do_Sul: 'MS',
    Minas_Gerais: 'MG',
    Pará: 'PA',
    Paraíba: 'PB',
    Paraná: 'PR',
    Pernambuco: 'PE',
    Piauí: 'PI',
    Rio_de_Janeiro: 'RJ',
    Rio_Grande_do_Norte: 'RN',
    Rio_Grande_do_Sul: 'RS',
    Rondônia: 'RO',
    Roraima: 'RR',
    Santa_Catarina: 'SC',
    São_Paulo: 'SP',
    Sergipe: 'SE',
    Tocantins: 'TO',
};

const parseField = (field): Field => {
    const parseLocation = (location) => {
        let newLocation = location.split(',');
        newLocation = newLocation.map((loc) => {
            return loc.trim();
        });
        const UF = newLocation[newLocation.length - 2];
        if (newLocation.length > 2 && UF.length !== 2) {
            const UFs = BRUFs;

            const match = Object.keys(UFs).find((Uf) => {
                Uf = Uf.replace(/_/g, ' ');
                return Uf === UF;
            });
            newLocation[newLocation.length - 2] = UFs[match];
            return newLocation.join(', ');
        }
        return location;
    };

    return {
        ...field,
        _id: String(field?.id),
        node: {
            ...field?.node,
            zoneIds: (field?.node?.zones || []).map((z) => z.id),
            crops: (field?.node?.zones || [])
                .map((z) => z.type)
                .filter((t) => ['corn', 'soy', 'cotton', 'refuge', 'crop_protection'].includes(t)),
        },
        location: field.country === 4063 ? parseLocation(field.location) : field.location,
        activeCropMonitoring: field?.cropMonitoring?.find((cropMonitoring: any) => cropMonitoring.active),
        geometry: isOldPolygon(field.geometry) ? [field.geometry] : field.geometry,
    };
};

export const getFieldsFromAPI = async ({
    page,
    pageSize = 10,
    name,
    country,
    geometryType,
    ordering,
    selectOnly,
    userNodeId,
    includeChildren,
    excludeTargetNode,
    cmCornPhenologyStage,
    cmPlantingDateFrom,
    cmPlantingDateTo,
    campaign,
    crop,
    active,
    activeCrop,
    idIn,
    nodeType,
}: {
    page: number;
    pageSize?: number;
    name?: string;
    country?: string;
    geometryType?: 'point' | 'polygon';
    ordering?:
        | 'name'
        | '-name'
        | 'location'
        | '-location'
        | 'planting_date'
        | '-planting_date'
        | 'harvest_date'
        | '-harvest_date';
    selectOnly?: string;
    userNodeId?: number;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
    cmCornPhenologyStage?: string;
    cmPlantingDateFrom?: string;
    cmPlantingDateTo?: string;
    campaign?: number;
    crop?: CropType;
    active?: boolean;
    activeCrop?: CropType | 'fallow';
    idIn?: string;
    nodeType?: string;
}): Promise<PaginatedResponse<Field>> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/me/`, window.location.origin);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    if (name) params.append('name', name);
    if (country) params.append('country', country);
    if (geometryType) params.append('geometry_type', geometryType);
    if (ordering) params.append('ordering', ordering);
    if (selectOnly) params.append('select_only', `${selectOnly},userNode`);
    if (userNodeId) params.append('node', userNodeId.toString());
    if (includeChildren !== undefined) params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (cmCornPhenologyStage) params.append('cm_corn_phenology_stage', cmCornPhenologyStage);
    if (cmPlantingDateFrom) params.append('cm_planting_date_from', cmPlantingDateFrom);
    if (cmPlantingDateTo) params.append('cm_planting_date_to', cmPlantingDateTo);
    if (campaign) params.append('campaign', String(campaign));
    if (crop) params.append('crop', crop);
    if (active !== undefined) params.append('active', String(active));
    if (activeCrop) params.append('active_crop', activeCrop);
    if (idIn) params.append('id__in', idIn);
    if (nodeType) params.append('node_type', nodeType);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());

    return {
        ...res.data,
        results: res.data.results
            .filter((f) => {
                if (!selectOnly || selectOnly.includes('node')) return f.node;
                return true;
            })
            .map(parseField),
    };
};

export const getFieldsByProductFromAPI = async ({
    page,
    pageSize = 10000,
    country,
    product_id,
    climate_pattern,
    planting_date,
    soil_moisture,
    userNodeId,
    includeChildren,
    excludeTargetNode,
}: {
    page: number;
    pageSize?: number;
    country?: string;
    product_id?: number;
    climate_pattern: string;
    planting_date: string;
    soil_moisture?: string;
    userNodeId?: number;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/me/with-corn-yield/`, window.location.origin);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    if (country) params.append('country', country);
    if (product_id) params.append('product_id', String(product_id));
    if (climate_pattern) params.append('climate_pattern', climate_pattern);
    if (planting_date) params.append('planting_date', planting_date);
    if (soil_moisture) params.append('soil_moisture', soil_moisture);
    if (userNodeId) params.append('user_node', String(userNodeId));
    if (includeChildren !== undefined) params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());

    return {
        ...res.data,
        results: res.data.results?.map(parseField) || [],
    };
};

export const getFieldFromAPI = async ({ fieldId }) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/me/${fieldId}/`, window.location.origin);
    const res = await axFetch.get(apiUrl.toString());
    return parseField(res.data);
};

export const updateFieldFromAPI = async ({
    fieldId,
    name,
    area,
    enabledSendFrostRiskNotification,
    country,
    userNodeId,
}: {
    fieldId: number;
    name: string;
    area: number;
    enabledSendFrostRiskNotification: boolean;
    country: string;
    userNodeId: number;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/me/${fieldId}/`, window.location.origin);
    const res = await axFetch.patch(apiUrl.toString(), {
        name,
        area,
        enabledSendFrostRiskNotification,
        user_node: userNodeId,
    });
    revalidateMyFields({ country, fieldId, fieldPayload: res.data });
    return parseField(res.data);
};

export const assignFieldsToGrowerFromAPI = async ({
    fieldIds,
    userNodeId,
    country,
}: {
    fieldIds: number[];
    userNodeId: number;
    country: string;
}) => {
    const results = await Promise.all(
        fieldIds.map((fieldId) => {
            const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/me/${fieldId}/`, window.location.origin);
            return axFetch.patch(apiUrl.toString(), { user_node: userNodeId }).then((res) => parseField(res.data));
        })
    );
    revalidateMyFields({ country });
    return results;
};

export const createFieldFromAPI = async ({
    name,
    geometry,
    appInstance,
    altitude,
    area,
    country,
    userNode,
}: {
    name: string;
    geometry: FormattedGeometry | FormattedGeometry[];
    appInstance: number;
    altitude: number;
    area: number;
    country: string;
    userNode?: number;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/me/`, window.location.origin);
    const res = await axFetch.post(apiUrl.toString(), {
        name,
        geometry,
        app_instance: appInstance,
        altitude,
        area: area ? area : undefined,
        user_node: userNode ? userNode : undefined,
    });
    revalidateMyFields({ country, fieldPayload: res.data });
    return parseField(res.data);
};

export const deleteFieldFromAPI = async ({ fieldId, country }) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/me/${fieldId}/`, window.location.origin);
    const res = await axFetch.delete(apiUrl.toString());
    revalidateMyFields({ country, fieldId });
    return res.data;
};

/**
 * Fetch FieldView fields from the fieldview API.
 * Optionally scope by fieldview_id and include timing debug.
 */
export const getFieldViewFieldsFromAPI = async ({
    fieldviewId,
    debug = false,
}: {
    fieldviewId?: number;
    debug?: boolean;
} = {}): Promise<FieldViewFieldsResponse> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/fieldview/`, window.location.origin);
    const params = new URLSearchParams();
    if (fieldviewId) params.append('fieldview_id', fieldviewId);
    if (debug) params.append('debug', '1');
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

/**
 * Import FieldView fields by fieldview_id and fieldview_field_ids.
 * POST to /core/v1/fields/fieldview/import/
 * Country code is taken from each field's properties.countryCode on the backend.
 */
export const importFieldViewFieldsFromAPI = async ({
    fieldviewId,
    fieldviewFieldIds,
}: {
    fieldviewId: string | number;
    fieldviewFieldIds: string[];
}): Promise<FieldViewImportFieldsResponse> => {
    const apiUrl = `${settings.urls.apiDomain}/core/v1/fields/fieldview/import/`;
    const res = await axFetch.post(apiUrl, {
        fieldview_id: String(fieldviewId),
        fieldview_field_ids: fieldviewFieldIds,
    });
    return res.data;
};

const getFieldMaxAndMinDatesZones = async ({ country }: { country: string }) => {
    try {
        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/fields/zone-dates/?country=${country}`,
            window.location.origin
        );
        const res = await axFetch.get(apiUrl.toString());
        return res.data;
    } catch (error) {
        return {};
    }
};

export const useGetFieldMaxAndMinDatesZones = ({ country }: { country: string }) => {
    let key = { country, key: 'useGetFieldMaxAndMinDatesZones' };
    if (!country) key = null;
    return useSWRImmutable(key, ({ country }) => getFieldMaxAndMinDatesZones({ country }));
};

const getFieldZone = async ({ lat, lng, crop }: { lat: number; lng: number; crop: string }) => {
    try {
        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/zones/?lat=${lat}&lng=${lng}&zone_type=${crop}`,
            window.location.origin
        );
        const res = await axFetch.get(apiUrl.toString());
        return res.data;
    } catch (error) {
        return [];
    }
};

export const useGetFieldZone = ({ lat, lng, crop }: { lat: number; lng: number; crop: string }) => {
    let key = { lat, lng, crop, key: 'useGetFieldZone' };
    if (!lat || !lng || !crop) key = null;
    return useSWRImmutable(key, ({ lat, lng, crop }) => getFieldZone({ lat, lng, crop }));
};
